/**
 * Beauty Bundle Guard — enforces all-or-nothing for the Buy 3 Get 3 FREE Gifts offer.
 *
 * Policy (Piers 2026-10-15): removing any part of an active Beauty Bundle clears the ENTIRE cart.
 *
 * Detection: monkeypatches window.fetch to catch every /cart/{change,update,add,remove,clear}.js
 * call — this is reliable across Dawn, cart drawers, apps, and ad-hoc jQuery scripts, where
 * event-based detection (cart:updated / PUB_SUB_EVENTS.cartUpdate) is inconsistent.
 *
 * Mechanism:
 *   1. Count cart items that carry properties["Beauty Bundle"] (tag applied by the PDP ATC JS).
 *   2. If the count drops from N>0 to a smaller N (but still >0), the bundle was partially removed.
 *   3. Call /cart/clear.js and hard-reload the page so every UI surface shows the empty cart.
 */
(function () {
  'use strict';
  if (window.__beautyBundleGuardInstalled) return;
  window.__beautyBundleGuardInstalled = true;

  const PROP_KEY = 'Beauty Bundle';
  // NATIVE FREE GIFTS (2026-10-18): gift lines are added by the PDP ATC JS (replacing Kaching's gift rules).
  // They must never check out on their own: no tub in the cart → remove every gift line; free one-time tub
  // present (property "Buy 2 Get 1 FREE", no selling plan) with fewer than 2 subscription tubs → remove it.
  const TUB_PRODUCT_ID = 11023494545751;                         // 2-in-1 Booty & Beauty Blend
  const GIFT_VARIANTS = { 54145653539159: 1, 54145653604695: 1, 54145653670231: 1, 54145653735767: 1 }; // bottle, whisk, tote, guide
  const GIFT_PROP = 'Free gift';
  const FREE_TUB_PROPS = ['Buy 2 Get 1 FREE', 'Buy 3 Get 2 FREE'];   // free ONE-TIME tubs carry one of these
  const PAID_TUB_PROPS = ['Buy 2 Get 1 FREE', 'Beauty Bundle'];       // subscription tubs of the Buy 2 / Buy 3 tiers
  const LOG = '[beauty-bundle-guard]';
  let lastCount = null;
  let busy = false;
  let lastItemCount = 0;   // total item quantity at the last check (kept for logging)
  let lastOfferQty = 0;    // quantity of OFFER lines (tubs + free gifts) at the last check: the all-or-nothing rule
                           // watches these only, so adding/removing a paid add-on (e.g. the £10 electrolytes) never clears the cart (2026-10-29 fix)
  let origFetchRef = window.fetch;   // set to the un-patched fetch below

  function countBundleItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce(function (n, it) {
      const props = it.properties || {};
      // Shopify may serialise properties as an array of [key,value] pairs in some contexts.
      if (Array.isArray(props)) {
        return props.some(function (p) { return Array.isArray(p) && p[0] === PROP_KEY; }) ? n + (it.quantity || 1) : n;
      }
      return props[PROP_KEY] ? n + (it.quantity || 1) : n;
    }, 0);
  }

  function hasProp(it, key) {
    const props = it.properties || {};
    if (Array.isArray(props)) return props.some(function (p) { return Array.isArray(p) && p[0] === key; });
    return !!props[key];
  }
  function isGiftLine(it) { return GIFT_VARIANTS[it.variant_id] === 1 || hasProp(it, GIFT_PROP); }
  function isTub(it) { return it.product_id === TUB_PRODUCT_ID && !isGiftLine(it); }
  function isSubLine(it) { return !!(it.selling_plan_allocation && it.selling_plan_allocation.selling_plan); }
  function isFreeTub(it) { return isTub(it) && !isSubLine(it) && FREE_TUB_PROPS.some(function (p) { return hasProp(it, p); }); }
  // Display rank, top → bottom: paid tubs, paid add-ons (e.g. the £10 electrolytes), free tubs, bottle, tote, whisk, guide.
  // Shopify lists the most recently added line FIRST, so a cart is "in order" when ranks never decrease down the list.
  function lineRank(it) {
    if (isTub(it) && isSubLine(it)) return 1;
    if (isFreeTub(it)) return 3;
    if (GIFT_VARIANTS[it.variant_id] === 1) return { 54145653539159: 4, 54145653670231: 5, 54145653604695: 6, 54145653735767: 7 }[it.variant_id] || 8;
    if (isGiftLine(it)) return 8;
    return 2;
  }
  function isOfferLine(it) { return isTub(it) || isGiftLine(it); }
  function offerQty(cart) { return cart.items.reduce(function (n, it) { return isOfferLine(it) ? n + (it.quantity || 0) : n; }, 0); }
  // Order is checked across OFFER lines only: a paid add-on sitting above the tubs is fine (the drawer sorts it for display),
  // so adding one no longer triggers a full clear + re-add of the cart (2026-10-29: removed the jitter on upsell add).
  function isOrdered(cart) { var last = 0; return cart.items.filter(isOfferLine).every(function (it) { var r = lineRank(it); if (r < last) return false; last = r; return true; }); }
  var resequencing = false;
  // Rebuild the cart in display order: clear, then add in REVERSE rank so the paid tubs end up newest (top).
  async function resequence(cart) {
    if (resequencing) return false;
    resequencing = true;
    try {
      var lines = cart.items.map(function (it, i) { var o = { id: it.variant_id, quantity: it.quantity, properties: it.properties || {}, _rank: lineRank(it), _i: i }; if (isSubLine(it)) o.selling_plan = it.selling_plan_allocation.selling_plan.id; return o; });
      lines.sort(function (a, b) { return (b._rank - a._rank) || (b._i - a._i); });
      lines.forEach(function (o) { delete o._rank; delete o._i; });
      console.log(LOG, 'RE-SEQUENCING cart into display order', lines.map(function (o) { return o.id + (o.selling_plan ? '/sub' : ''); }));
      var clr = await origFetchRef('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!clr.ok) throw new Error('clear ' + clr.status);
      var add = null;
      for (var attempt = 0; attempt < 2 && !(add && add.ok); attempt++) {
        add = await origFetchRef('/cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: lines }) });
      }
      if (!add.ok) throw new Error('re-add ' + add.status);
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') { try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'offer-guard' }); } catch (e) {} }
      return true;
    } finally { resequencing = false; }
  }

  // ALL-OR-NOTHING (Piers 2026-10-18, extending the 2026-10-15 Beauty Bundle policy to every offer): if the cart
  // holds any line from our offers (a gift line, a free tub, a Beauty Bundle tub) and the customer removes ANYTHING
  // or lowers any quantity, the ENTIRE cart is cleared. A cart that holds gift lines but no tub, or a free one-time
  // tub without 2 subscription tubs (assembled outside the PDP), is cleared outright.
  function hasOfferLines(cart) {
    return cart.items.some(function (it) { return isGiftLine(it) || isFreeTub(it) || PAID_TUB_PROPS.some(function (p) { return hasProp(it, p); }); });
  }
  function offerCartIsInvalid(cart) {
    const tubs = cart.items.filter(isTub);
    const subTubQty = tubs.filter(isSubLine).reduce(function (n, it) { return n + (it.quantity || 0); }, 0);
    if (tubs.length === 0 && cart.items.some(isGiftLine)) return 'gift lines without a tub';
    const freeTubQty = tubs.filter(isFreeTub).reduce(function (n, it) { return n + (it.quantity || 0); }, 0);
    const paidTubQty = tubs.filter(function (it) { return !isFreeTub(it); }).reduce(function (n, it) { return n + (it.quantity || 0); }, 0);   // subscription OR one-time paid tubs
    const allowedFree = paidTubQty >= 3 ? 2 : paidTubQty >= 2 ? 1 : 0;   // Buy 3 → 2 free, Buy 2 → 1 free (sub or one-time)
    if (freeTubQty > allowedFree) return 'free tubs without enough paid tubs (' + freeTubQty + ' free, ' + paidTubQty + ' paid)';
    return null;
  }
  async function clearEverything(why) {
    console.log(LOG, 'CLEARING CART —', why);
    const clr = await origFetchRef('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!clr.ok) throw new Error('clear ' + clr.status);
    lastCount = 0; lastItemCount = 0; lastOfferQty = 0;
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'offer-guard' }); } catch (e) {}
    }
    setTimeout(function () { window.location.reload(); }, 150);
  }

  // Triggers (fetch, XHR, cart events, pub/sub) are coalesced: one /cart.js read per burst, and a burst that
  // arrives while a check is running re-runs it afterwards instead of being dropped.
  let schedTimer = null, dirty = false;
  function scheduleCheck(reason) { clearTimeout(schedTimer); schedTimer = setTimeout(function () { checkCart(reason); }, 60); }
  async function checkCart(reason) {
    if (busy) { dirty = true; return; }
    busy = true;
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!r.ok) throw new Error('cart.js ' + r.status);
      const cart = await r.json();
      const itemCount = cart.item_count || 0;
      const offer = hasOfferLines(cart);
      const invalid = offer ? offerCartIsInvalid(cart) : null;
      if (invalid) { await clearEverything(invalid); return; }
      const oq = offerQty(cart);
      if (offer && lastOfferQty > 0 && oq > 0 && oq < lastOfferQty) { await clearEverything('part of the offer was removed (' + lastOfferQty + ' → ' + oq + ' offer items)'); return; }
      lastItemCount = itemCount; lastOfferQty = oq;
      if (offer && itemCount > 1 && !isOrdered(cart)) { await resequence(cart); return; }
      const current = countBundleItems(cart);
      console.log(LOG, 'check (' + (reason || 'init') + '):', { lastCount: lastCount, current: current, totalItems: cart.item_count });

      if (lastCount !== null && current > 0 && current < lastCount) {
        console.log(LOG, 'PARTIAL REMOVAL — clearing cart');
        const clr = await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (!clr.ok) throw new Error('clear ' + clr.status);
        lastCount = 0;
        // Notify listeners first, then hard-reload — reload is the belt-and-braces guarantee
        // that every UI surface (header count, drawer, cart page, app widgets) re-renders empty.
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
          try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'beauty-bundle-guard' }); } catch (e) {}
        }
        setTimeout(function () { window.location.reload(); }, 150);
        return;
      }
      lastCount = current;
    } catch (e) {
      console.warn(LOG, 'error', e);
    } finally {
      busy = false;
      if (dirty) { dirty = false; scheduleCheck('rerun'); }
    }
  }

  // Initial snapshot
  if (document.readyState !== 'loading') checkCart('init');
  else document.addEventListener('DOMContentLoaded', function () { checkCart('init'); });

  // ──────────────────────────────────────────────────────────
  // Monkeypatch window.fetch — catches EVERY cart mutation
  // ──────────────────────────────────────────────────────────
  const origFetch = window.fetch;
  origFetchRef = origFetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string'
      ? input
      : (input && input.url) || '';
    const promise = origFetch.apply(this, arguments);
    if (/\/cart\/(change|update|add|remove|clear)(\.js)?(\?|$)/i.test(url)) {
      // Give the native handler a tick to update DOM state, then re-check
      promise.then(function () { scheduleCheck('fetch:' + url.split('?')[0].split('/').pop()); })
             .catch(function () {});
    }
    return promise;
  };

  // ──────────────────────────────────────────────────────────
  // Fallback: XMLHttpRequest (older apps / plugins)
  // ──────────────────────────────────────────────────────────
  try {
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (typeof url === 'string' && /\/cart\/(change|update|add|remove|clear)(\.js)?(\?|$)/i.test(url)) {
        this.addEventListener('loadend', function () { scheduleCheck('xhr'); });
      }
      return origOpen.apply(this, arguments);
    };
  } catch (e) { /* ignore */ }

  // ──────────────────────────────────────────────────────────
  // Fallback: event listeners (defensive — may or may not fire)
  // ──────────────────────────────────────────────────────────
  document.addEventListener('cart:updated', function () { scheduleCheck('cart:updated'); });
  document.addEventListener('cart:refresh', function () { scheduleCheck('cart:refresh'); });
  document.addEventListener('cart:change',  function () { scheduleCheck('cart:change'); });

  function trySubscribePubSub() {
    if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      try { window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () { scheduleCheck('pubsub'); }); } catch (e) {}
      return true;
    }
    return false;
  }
  if (!trySubscribePubSub()) {
    let tries = 0;
    const timer = setInterval(function () {
      if (trySubscribePubSub() || ++tries > 20) clearInterval(timer);
    }, 250);
  }
})();
